import { Box, Button, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import gotonSrc from '~/assets/goton/goton-aseri.svg'
import logoSrc from '~/assets/logo/logo.svg'
import {
  KioskErrorContent,
  KioskRetryFailedAlert,
} from '~/components/layout/KioskErrorScreen/KioskErrorScreen'
import { useKioskRetry } from '~/components/layout/KioskErrorScreen/hooks/useKioskRetry'
import { PageLayout } from '~/components/layout/PageLayout/PageLayout'
import { appMode } from '~/config'
import { useLogout } from '~/domain/logout/useLogout'
import { getQueryKey } from '~/lib/tanstackQuery/queryKey'
import type { GetMeResponse } from '@ulysses-inc/harami_api_client'

export function EmptyPlaceListContent() {
  return appMode.isKiosk ? (
    <KioskEmptyPlaceListContent />
  ) : (
    <UserEmptyPlaceListContent />
  )
}

function UserEmptyPlaceListContent() {
  const { logout } = useLogout()

  return (
    <PageLayout header={{ component: null }} mainLayoutType="center">
      <Box
        sx={{
          width: '100%',
          maxWidth: '360px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box component="img" src={logoSrc} sx={{ width: '280px' }} />
        <Typography
          variant="h3"
          sx={{ mt: 7, lineHeight: 1.6, textAlign: 'center' }}
        >
          所属現場が設定されておりません。
          <br />
          管理者にお問い合わせください。
        </Typography>
        <Box component="img" src={gotonSrc} sx={{ width: '50%', mt: 5 }} />
        <Button
          onClick={async () => {
            await logout({ userInitiated: true })
          }}
          variant="contained"
          sx={{ width: '100%', mt: 5 }}
          size="large"
        >
          ログアウトする
        </Button>
      </Box>
    </PageLayout>
  )
}

/**
 * キオスク端末（kiosk）の Device が harami の現場に配属されていない場合の画面。
 * Device には「ログアウトする」操作が存在しないため、Rump ガイドラインの
 * ひな形（管理者側の設定が必要 → 再試行）に合わせる。Figma に Harami の例として掲載されている文言。
 * 再試行は GET /me を再取得し、配属が反映されていれば現場設定へ進む。
 */
function KioskEmptyPlaceListContent() {
  const queryClient = useQueryClient()

  const { isRetrying, isRetryFailed, retry } = useKioskRetry(async () => {
    const queryKey = getQueryKey('/me', 'GET')
    await queryClient.invalidateQueries({ queryKey })
    // 成否は再取得した /me のレスポンスから直接判定する。place store は useSyncedAuthState の
    // effect が後から反映するため、ここで store を読むと古い空配列を見て失敗扱いになり得る。
    // 配属があれば store 反映後に PlaceListPageRoute がこの画面を外す
    const me = queryClient.getQueryData<GetMeResponse>(queryKey)
    return (me?.placeNodes.length ?? 0) > 0
  })

  return (
    <PageLayout header={{ component: null }} mainLayoutType="center">
      <KioskErrorContent
        title="所属現場が設定されていないため、ご利用いただけません"
        paragraphs={[
          '端末を管理している社内のシステム管理者までご連絡ください。',
          '設定が完了した後に、以下の「再試行」を押すとご利用いただけます。',
        ]}
        alert={
          isRetryFailed && (
            <KioskRetryFailedAlert message="所属現場がまだ設定されていません。社内のシステム管理者へご連絡ください。" />
          )
        }
        action={
          <Button
            onClick={retry}
            loading={isRetrying}
            variant="contained"
            size="large"
          >
            再試行
          </Button>
        }
      />
    </PageLayout>
  )
}
